import { computed, inject, Injectable, signal } from '@angular/core';
import { SplitService } from './split.service';
import { isEmpty, round } from 'lodash';
import {
  Payment,
  GroupedPayment,
  OrderItem,
  PartiallyGroupedPayment,
  PaymentDetail,
  Transaction,
} from '@tabify/models/types';
import { SplitType } from '@tabify/models/enums';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class OrderService {
  private readonly splitService = inject(SplitService);
  private readonly vatPercentage = 0.25; // 25% VAT

  transactionId = signal<string | null>(null);
  groupId = signal<string | null>(null);
  tableShortCode = signal<string | null>(null);
  /** GUID identifying the partner (chain). Null until a transaction with a valid GUID populates. */
  partnerId = signal<string | null>(null);
  /**
   * GUID identifying the branch (location). Null until a transaction with a
   * valid GUID populates. Drives per-branch Worldline gateway merchant ID
   * lookup and gates Apple Pay / Google Pay availability — see
   * `walletPaymentEnabled` below.
   */
  partnerBranchId = signal<string | null>(null);
  /**
   * True when the loaded transaction provides a valid `partnerBranchId` AND
   * that branch has a configured Worldline gateway merchant ID override in
   * `environment.worldline.branchMerchantIds`.
   *
   * PaymentComponent filters Apple Pay + Google Pay out of the payment
   * options list when this is false. There is no platform-wide fallback —
   * a branch that isn't in the override map can't initiate wallet payments
   * (the gateway-tokenized payload would be encrypted for a merchant the
   * backend isn't using).
   */
  walletPaymentEnabled = computed(() => {
    const branchId = this.partnerBranchId();
    if (!branchId) return false;
    return Object.prototype.hasOwnProperty.call(
      environment.worldline.branchMerchantIds,
      branchId,
    );
  });
  partnerName = signal<string>('');
  branchName = signal<string>('');
  restaurantName = signal<string>('');
  logoUrl = signal<string | null>(null);
  bannerUrl = signal<string | null>(null);
  hasApiError = signal(false);
  websiteUrl = signal<string | null>(null);
  facebookUrl = signal<string | null>(null);
  instagramUrl = signal<string | null>(null);
  // Missing or null from the API collapses to [], so an unknown list hides features.
  features = signal<string[]>([]);
  tableNumber = signal<number>(0);
  // tableNumber = signal<number>(this.generateRandomTableNumber());
  // originalOrderItems = signal<OrderItem[]>(this.generateRandomItems());
  originalOrderItems = signal<OrderItem[]>([]);
  payments = signal<Payment[]>([]);
  // paidOrderItems = signal<PaidOrderItem[]>([]);
  selectedOrderItems = signal<OrderItem[]>([]);
  amountAlreadyPaid = signal<number>(0);
  customerPaid = signal<number>(0);
  customerTip = signal<number>(0);

  paidOrderItems = computed(() => {
    return this.payments()
      .map((payment) => {
        return payment.orderItems.map((item) => ({
          ...item,
          paymentId: payment.id,
          groupId: payment.groupId,
          type: payment.type,
          splitCount: payment.splitCount,
          payForSplitCount: payment.payForSplitCount,
        }));
      })
      .flat();
  });

  /**
   * Computed signal that calculates remaining unpaid items available for payment.
   *
   * Logic:
   * - Takes originalOrderItems from API
   * - Uses mergeGroupedPayments to deduplicate payments with same groupId
   * - For each item, calculates paid quantity from merged payments
   * - Deducts paid quantity to get remaining quantity
   * - Sets hasPartialPayment flag for incomplete split payments only
   * - Returns all items including those with quantity = 0 (for UI strikeout display)
   *
   * @returns OrderItem[] - Array of all order items with calculated remaining quantities
   */
  orderItems = computed(() => {
    // Get deduplicated payments (same groupId are merged)
    const mergedPayments = this.mergeGroupedPayments(null);

    const remainingItems = this.originalOrderItems().map((item: OrderItem) => {
      // Calculate how much of this item has been paid
      let paidQuantity = 0;
      let paidAmount = 0;
      let hasPartialPayment = false;
      let partialPaymentQuantity = 0; // Track quantity locked in incomplete splits

      mergedPayments.forEach((payment) => {
        const paidItem = payment.items.find((i) => i.id === item.id);

        if (paidItem) {
          if (payment.type === SplitType.SELECTED_ITEMS) {
            // Full item payment - deduct quantity immediately
            paidQuantity += paidItem.quantity;
            paidAmount += paidItem.totalPrice;

            // Add extras amount
            for (const extra of paidItem.extras || []) {
              paidAmount += extra.totalPrice;
            }
          } else if (
            payment.type === SplitType.SPLIT_SELECTED_ITEMS_EVENLY ||
            payment.type === SplitType.SPLIT_EVENLY
          ) {
            // Split payment - calculate proportional amount
            paidAmount +=
              paidItem.totalPrice *
              (payment.payForSplitCount / payment.splitCount);

            // Add proportional extras amount
            for (const extra of paidItem.extras || []) {
              paidAmount +=
                extra.totalPrice *
                (payment.payForSplitCount / payment.splitCount);
            }

            // Only deduct quantity if split is complete
            if (payment.payForSplitCount >= payment.splitCount) {
              paidQuantity += paidItem.quantity;
            } else {
              // Mark as partial if split is incomplete (don't deduct quantity)
              hasPartialPayment = true;
              partialPaymentQuantity += paidItem.quantity; // Track locked quantity
            }
          }
        }
      });

      const remainingQuantity = item.quantity - paidQuantity;

      // Calculate remaining extras based on remaining quantity
      const extras = item.extras?.map((extra) => ({
        ...extra,
        quantity: remainingQuantity,
        totalPrice: extra.unitPrice * remainingQuantity,
      }));

      // Calculate total price for remaining quantity
      const itemTotalPrice = item.unitPrice * remainingQuantity;
      const extrasTotalPrice =
        extras?.reduce((sum, extra) => sum + extra.totalPrice, 0) || 0;
      const totalPrice = itemTotalPrice + extrasTotalPrice;

      return {
        ...item,
        extras: extras || [],
        quantity: remainingQuantity,
        totalPrice,
        paidAmount,
        hasPartialPayment,
        partialPaymentQuantity,
      };
    });

    return remainingItems;
  });

  originalSubtotal = computed(() => {
    const orderItems = [...this.originalOrderItems()];

    // Calculate item total + extras total (extras are not included in item.totalPrice from API)
    return orderItems
      .filter((item) => item.quantity > 0)
      .reduce((total, item) => {
        const itemTotal = item.totalPrice;
        const extrasTotal =
          item.extras?.reduce((sum, extra) => sum + extra.totalPrice, 0) || 0;
        return total + itemTotal + extrasTotal;
      }, 0);
  });

  originalVatAmount = computed(() => {
    return this.originalSubtotal() * this.vatPercentage;
  });

  originalRemainingAmount = computed(() => {
    const remaining = this.originalTotal() - this.totalAmountAlreadyPaid();
    // Round to 2 decimal places to handle floating-point precision errors
    // Ensure we never show negative values (treat < 0.01 as 0)
    return Math.max(0, round(remaining, 2));
  });

  originalTotal = computed(() => {
    // TODO Clean up, VAT is already included
    return this.originalSubtotal(); // + this.originalVatAmount();
  });

  /**
   * Computed signal that calculates the remaining subtotal excluding VAT.
   * Uses orderItemsSubtotal which calculates from actual remaining items (quantity > 0).
   * Since all prices include VAT, we divide by 1.25 to get the base price.
   *
   * @returns number - Remaining subtotal without VAT
   */
  remainingSubtotalWithoutVat = computed(() => {
    return this.orderItemsSubtotal() / 1.25;
  });

  /**
   * Computed signal that calculates the VAT amount from remaining unpaid items.
   * Uses orderItemsSubtotal which calculates from actual remaining items (quantity > 0).
   * Formula: remainingWithVat * (0.25 / 1.25) = remainingWithVat * 0.2
   *
   * @returns number - VAT amount for remaining items
   */
  remainingVatAmount = computed(() => {
    return this.orderItemsSubtotal() * 0.2;
  });

  orderItemsSubtotal = computed(() => {
    const orderItems = [...this.orderItems()];
    // Use totalPrice which now includes item price + extras
    return orderItems
      .filter((item) => item.quantity > 0)
      .reduce((total, item) => total + item.totalPrice, 0);
  });

  orderItemsVatAmount = computed(() => {
    return this.orderItemsSubtotal() * this.vatPercentage;
  });

  totalAmountAlreadyPaid = computed(() => {
    const payments = this.payments();

    if (isEmpty(payments)) {
      return 0;
    }

    const totalAmount = payments.reduce(
      (total, payment) => total + payment.subTotal,
      0,
    );

    // Only cap at `originalTotal` when there's a meaningful original to
    // compare against. On the transaction-details (receipt) view the BE
    // returns an empty `orderItems` array — items live on the individual
    // payments instead — which makes `originalTotal` zero. Capping there
    // would crush legitimate payment totals to 0 and hide the "Totalt
    // betalt" figure on closed transactions.
    const originalTotal = this.originalTotal();
    if (originalTotal > 0 && totalAmount > originalTotal) {
      return originalTotal;
    }

    return round(totalAmount, 2);
  });

  orderItemsRemainingAmount = computed(() => {
    return this.orderItemsTotal() - this.totalAmountAlreadyPaid();
  });

  orderItemsTotal = computed(() => {
    return this.orderItemsSubtotal();
  });

  /**
   * Returns the canonical SPLIT_EVENLY group on this transaction, if any.
   * "Canonical" = the merged Authorized/Captured SplitEvenly group, which
   * defines the splitCount that all subsequent SplitEvenly payments must match.
   * Used after a recoverable rejection to auto-reconcile the user's selection.
   */
  canonicalSplitEvenlyGroup = computed<{
    groupId: string;
    splitCount: number;
    payForSplitCount: number;
    hasSlotsAvailable: boolean;
  } | null>(() => {
    const groups = this.mergeGroupedPayments([SplitType.SPLIT_EVENLY]);
    // Backend enforces at most one SPLIT_EVENLY group per transaction; pick
    // the first that actually has a non-null groupId.
    const group = groups.find((g) => g.groupId !== null);
    if (!group || !group.groupId) return null;
    return {
      groupId: group.groupId,
      splitCount: group.splitCount,
      payForSplitCount: group.payForSplitCount,
      hasSlotsAvailable: group.payForSplitCount < group.splitCount,
    };
  });

  selectedItemSubTotal = computed(() => {
    if (
      this.splitService.option() == 'SELECTED_ITEMS' ||
      this.splitService.option() == 'SPLIT_SELECTED_ITEMS_EVENLY'
    ) {
      return round(
        this.selectedOrderItems().reduce((total, item) => {
          const itemTotal = item.unitPrice * item.quantity;

          const extrasTotal =
            item.extras?.reduce(
              (sum, extra) => sum + extra.unitPrice * item.quantity,
              0,
            ) || 0;

          return total + itemTotal + extrasTotal;
        }, 0),
        2,
      );
    }
    return 0;
  });

  selectedOrderItemVatAmount = computed(() => {
    return this.selectedItemSubTotal() * this.vatPercentage;
  });

  selectedItemTotal = computed(() => {
    return this.selectedItemSubTotal() + this.selectedOrderItemVatAmount();
  });

  /**
   * Computed signal that calculates the subtotal of fully paid items.
   * Fully paid items are those where remaining quantity = 0 and not part of an incomplete split.
   *
   * @returns number - Total amount paid for fully paid items
   */
  fullyPaidItemsSubtotal = computed(() => {
    return this.orderItems()
      .filter(
        (item) =>
          item.quantity === 0 && item.paidAmount > 0 && !item.hasPartialPayment,
      )
      .reduce((total, item) => total + item.paidAmount, 0);
  });

  /**
   * Computed signal that calculates the UNPAID amount for incomplete split payments.
   * Groups payments by groupId and calculates total paid shares per group.
   * Only counts a split as incomplete if total paid shares < splitCount.
   *
   * @returns number - Total unpaid amount for incomplete splits
   */
  partiallyPaidItemsSubtotal = computed(() => {
    return this.groupedPayments()
      .filter(
        (group) =>
          (group.type === SplitType.SPLIT_SELECTED_ITEMS_EVENLY ||
            group.type === SplitType.SPLIT_EVENLY) &&
          group.groupId !== null,
      )
      .reduce((total, group) => {
        // Calculate total paid shares across all payments in this group
        const totalPaidShares = group.payments.reduce(
          (sum, payment) => sum + payment.payForSplitCount,
          0,
        );

        // Only include if group is incomplete (total paid < total required)
        if (totalPaidShares < group.splitCount) {
          const unpaidShares = group.splitCount - totalPaidShares;
          const unpaidAmount = unpaidShares * group.splitAmount;
          return total + unpaidAmount;
        }

        return total;
      }, 0);
  });

  /**
   * Computed signal that calculates the subtotal of unpaid items.
   * Includes all remaining items that are not part of incomplete split payments.
   * Uses quantity > 0 to determine remaining unpaid items.
   *
   * @returns number - Total price of unpaid items (remaining quantity × unit price)
   */
  unpaidItemsSubtotal = computed(() => {
    return this.orderItems()
      .filter((item) => item.quantity > 0 && !item.hasPartialPayment)
      .reduce((total, item) => total + item.totalPrice, 0);
  });

  /**
   * Computed signal that groups payments by groupId with nested payment details.
   *
   * Logic:
   * - For each payment, builds the paid order items
   * - Groups payments by their groupId
   * - Payments with same groupId are merged into one entry with nested payments array
   * - Payments with null groupId remain as separate entries
   * - Aggregates totalAmount and totalQuantity across grouped payments
   *
   * @returns GroupedPayment[] - Array of grouped payments with nested payment details
   */
  groupedPayments = computed(() => {
    const groupMap = new Map<string | null, GroupedPayment>();

    this.payments().forEach((payment) => {
      // The BE returns each extra both nested under its parent and as a flat
      // top-level entry in `payment.orderItems`. The previous join against
      // `originalOrderItems` silently filtered the flat copies out, but broke
      // for Cancelled transactions where `originalOrderItems` is `[]` by
      // design — every paid item failed the lookup and the UI rendered an
      // empty Items section. Source names + prices directly from
      // `payment.orderItems` (BE now ships `itemName` / `unitName` there) and
      // drop the flat copies by skipping any ID that also appears in some
      // parent's `extras[]`.
      const extrasIds = new Set<string>(
        payment.orderItems.flatMap((item) =>
          (item.extras ?? []).map((e) => e.id),
        ),
      );

      const paidOrderItems: OrderItem[] = payment.orderItems
        .filter((item) => !extrasIds.has(item.id))
        .map((paidItem) => {
          const extras: OrderItem[] = paidItem.extras
            ?.map((extra) => ({
              ...extra,
              extras: [],
              quantity: paidItem.quantity,
            })) ?? [];

          return {
            ...paidItem,
            extras,
            quantity: paidItem.quantity,
            totalPrice: paidItem.unitPrice * paidItem.quantity,
          };
        });

      const totalQuantity = paidOrderItems.reduce(
        (sum, item) => sum + item.quantity,
        0,
      );

      // Create payment detail for this payment
      const paymentDetail: PaymentDetail = {
        paymentId: payment.id,
        totalAmount: payment.subTotal,
        tipAmount: payment.tipAmount,
        payForSplitCount: payment.payForSplitCount,
        totalQuantity,
        items: paidOrderItems,
      };

      // Use groupId as key, or payment id for non-grouped payments
      const groupKey = payment.groupId ?? payment.id;

      const existingGroup = groupMap.get(groupKey);

      if (existingGroup) {
        // Add to existing group
        existingGroup.payments.push(paymentDetail);
        existingGroup.totalAmount += payment.subTotal;
        existingGroup.totalQuantity = totalQuantity;
      } else {
        // Create new group
        groupMap.set(groupKey, {
          groupId: payment.groupId,
          totalAmount: payment.subTotal,
          totalQuantity,
          splitCount: payment.splitCount,
          splitAmount: payment.splitAmount,
          type: payment.type,
          payments: [paymentDetail],
        });
      }
    });

    return Array.from(groupMap.values());
  });

  mergeGroupedPayments(
    filterType: SplitType[] | null,
  ): PartiallyGroupedPayment[] {
    const rows = [...this.groupedPayments()];
    const merged: PartiallyGroupedPayment[] = [];
    const filteredRows = filterType
      ? rows.filter((r) => filterType.includes(r.type) && r.groupId)
      : rows;

    for (const group of filteredRows) {
      // Flatten the nested payments to create a PartiallyGroupedPayment
      const allItems: OrderItem[] = [];
      let totalPayForSplitCount = 0;

      // Collect all items from nested payments
      for (const payment of group.payments) {
        totalPayForSplitCount += payment.payForSplitCount;

        // Merge items by id (dedupe; take max quantity)
        for (const item of payment.items) {
          const existingItem = allItems.find((i) => i.id === item.id);
          if (!existingItem) {
            allItems.push({ ...item });
          } else {
            // Keep the larger quantity to avoid overcounting
            existingItem.quantity = Math.max(
              existingItem.quantity,
              item.quantity,
            );
          }
        }
      }

      // Calculate total quantity from merged items
      const totalQuantity = allItems.reduce(
        (sum, item) => sum + item.quantity,
        0,
      );

      merged.push({
        groupId: group.groupId,
        totalAmount: group.totalAmount,
        totalQuantity,
        type: group.type,
        splitCount: group.splitCount,
        payForSplitCount: totalPayForSplitCount,
        splitAmount: group.splitAmount,
        items: allItems,
      });
    }
    return merged;
  }

  private readonly isExtrasEnabled = false; // This can be toggled based on your requirements

  /**
   * Returns a copy of `item` whose `quantity` is set to `parentQuantity` and
   * whose nested `extras` are synchronized to the same quantity (with their
   * `totalPrice` recomputed). Extras always belong to the parent line item
   * 1:1 — if the user picks 2 Burgers, that means 2 cheese + 2 bacon — so
   * the parent's quantity is the source of truth.
   */
  private withSyncedExtras(
    item: OrderItem,
    parentQuantity: number,
  ): OrderItem {
    return {
      ...item,
      quantity: parentQuantity,
      extras:
        item.extras?.map((extra) => ({
          ...extra,
          quantity: parentQuantity,
          totalPrice: extra.unitPrice * parentQuantity,
        })) ?? [],
    };
  }

  increaseSelectedItemQuantity(item: OrderItem): void {
    // Read availability from the live `orderItems()` computed rather than
    // trusting the `item` arg, which may be a stale snapshot taken at the
    // page's initial render. Subtract `partialPaymentQuantity` so the guard
    // agrees with what `OrderItemSelectionComponent.showIncreaseButton()`
    // already uses to hide the `+` button.
    const liveItem = this.orderItems().find((i) => i.id === item.id);
    if (!liveItem) return;

    const availableQuantity =
      liveItem.quantity - (liveItem.partialPaymentQuantity ?? 0);

    const existingItem = this.selectedOrderItems().find(
      (selectedItem) => selectedItem.id === item.id,
    );
    const currentSelected = existingItem?.quantity ?? 0;

    // Multi-tap backstop: the template's `@if (showIncreaseButton)` hides the
    // button at the limit, but rapid taps can fire additional click events
    // before Angular re-renders and removes the button. Return silently —
    // the next CD cycle hides the button.
    if (currentSelected >= availableQuantity) {
      return;
    }

    if (existingItem) {
      this.selectedOrderItems.update((items) =>
        items.map((selectedItem) =>
          selectedItem.id === item.id
            ? this.withSyncedExtras(selectedItem, selectedItem.quantity + 1)
            : selectedItem,
        ),
      );
    } else {
      this.selectedOrderItems.update((items) => [
        ...items,
        this.withSyncedExtras(liveItem, 1),
      ]);
    }
  }

  decreaseSelectedItemQuantity(item: OrderItem): void {
    const existingItem = this.selectedOrderItems().find(
      (selectedItem) => selectedItem.id === item.id,
    );

    if (existingItem && existingItem.quantity > 1) {
      this.selectedOrderItems.update((items) =>
        items.map((selectedItem) =>
          selectedItem.id === item.id
            ? this.withSyncedExtras(selectedItem, selectedItem.quantity - 1)
            : selectedItem,
        ),
      );
    } else {
      this.selectedOrderItems.update((items) =>
        items.filter((selectedItem) => selectedItem.id !== item.id),
      );
    }
  }

  clearSelectedItems(): void {
    this.selectedOrderItems.set([]);
  }

  /**
   * Populate order state signals from a Transaction API response.
   * Used on initial load and when refreshing after a split-payment rejection.
   */
  populate(transaction: Transaction): void {
    this.transactionId.set(transaction.transactionId);
    this.partnerId.set(this.parseGuidOrNull(transaction.partnerId, 'partnerId', transaction.transactionId));
    this.partnerBranchId.set(this.parseGuidOrNull(transaction.partnerBranchId, 'partnerBranchId', transaction.transactionId));
    this.partnerName.set(transaction.partnerName);
    this.branchName.set(transaction.branchName);
    this.restaurantName.set(transaction.displayName);
    this.logoUrl.set(transaction.logoUrl);
    this.bannerUrl.set(transaction.bannerUrl);
    this.websiteUrl.set(transaction.websiteUrl);
    this.facebookUrl.set(transaction.facebookUrl);
    this.instagramUrl.set(transaction.instagramUrl);
    this.features.set(transaction.features ?? []);
    this.originalOrderItems.set(transaction.orderItems);
    this.payments.set(transaction.payments);
    this.tableNumber.set(transaction.tableNumber);

    // After the bill state refreshes, clamp any in-progress selection that
    // exceeds the new availability (or drop entries for items no longer on
    // the bill). Without this, a race-rejection refresh would leave stale
    // selections that render as negative remaining quantities on the
    // item-selection screen and fail at POST time. The user already got the
    // rejection toast (`worldline.callback.items_unavailable.retry` etc.)
    // that explains *why* their bill changed — no second toast needed.
    this.reconcileSelectedItemsAgainstBill();
  }

  /**
   * Validate that a Transaction GUID field looks like a real GUID. Returns
   * the value if valid, null otherwise (and logs an error so ops sees it).
   * Treats missing field, empty string, and malformed UUIDs identically —
   * all three mean "we don't have a usable identifier here." For
   * `partnerBranchId` specifically, returning null disables wallet payments
   * for the transaction (see `walletPaymentEnabled`).
   */
  private parseGuidOrNull(
    value: string | undefined,
    fieldName: string,
    transactionId: string,
  ): string | null {
    if (typeof value === 'string' && OrderService.GUID_RE.test(value)) {
      return value;
    }
    console.error(
      `Transaction ${transactionId} missing or malformed ${fieldName} — ` +
        `wallet payments will be disabled on this transaction.`,
      { transactionId, fieldName, value },
    );
    return null;
  }

  private static readonly GUID_RE =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  /**
   * Clamp `selectedOrderItems` so each entry's quantity doesn't exceed the
   * currently available quantity on the bill, and drop entries for items
   * that are no longer on the bill (or whose availability dropped to 0).
   * Idempotent — safe to call repeatedly.
   */
  private reconcileSelectedItemsAgainstBill(): void {
    const current = this.selectedOrderItems();
    if (current.length === 0) return;

    const refreshedItems = this.orderItems();
    let changed = false;

    const reconciled = current
      .map((selected) => {
        const refreshed = refreshedItems.find((i) => i.id === selected.id);
        if (!refreshed) {
          // Item was voided at POS between selection and refresh.
          changed = true;
          return null;
        }
        const available =
          refreshed.quantity - (refreshed.partialPaymentQuantity ?? 0);
        if (selected.quantity > available) {
          changed = true;
          return available > 0
            ? this.withSyncedExtras(selected, available)
            : null;
        }
        return selected;
      })
      .filter((item): item is OrderItem => item !== null);

    if (changed) {
      this.selectedOrderItems.set(reconciled);
    }
  }

  reset() {
    this.transactionId.set(null);
    this.groupId.set(null);
    this.tableShortCode.set(null);
    this.partnerId.set(null);
    this.partnerBranchId.set(null);
    this.partnerName.set('');
    this.branchName.set('');
    this.restaurantName.set('');
    this.hasApiError.set(false);
    this.logoUrl.set(null);
    this.bannerUrl.set(null);
    this.websiteUrl.set(null);
    this.facebookUrl.set(null);
    this.instagramUrl.set(null);
    this.features.set([]);
    this.tableNumber.set(0);
    this.originalOrderItems.set([]);
    this.payments.set([]);
    this.selectedOrderItems.set([]);
  }
}
